import { loader } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import { EVENTS, scaleValue } from "@folio-2026/shared"
import gsap from "gsap"
import { color, uniform } from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import { edgeFade } from "./utils/timelineShader"

const dummyObject = new THREE.Object3D()

// Default fade margin values
const DESKTOP_FADE_START_MARGIN = 0.15
const DESKTOP_FADE_END_MARGIN = 0.5
const MOBILE_FADE_START_MARGIN = 0
const MOBILE_FADE_END_MARGIN = 0.1
const MOBILE_BREAKPOINT = 768

const settings = {
  overallOpacity: uniform(0),
  fadeStartMargin: uniform(0.15),
  fadeEndMargin: uniform(0.5),
  // Big lines settings
  bigLineHeight: uniform(80),
  bigLineWidth: uniform(1),
  bigLineColor: uniform(color("#3d547f")),
  // Small lines settings
  smallLineHeight: uniform(40),
  smallLineWidth: uniform(1),
  smallLineColor: uniform(color("#3c3b3b")),
}

export default class Timeline {
  experience: Experience
  scene: THREE.Scene
  element: HTMLDivElement
  bounds!: DOMRect
  y!: number
  targetContainer?: HTMLElement
  targetInitialViewportX: number = 0
  targetInitialViewportY: number = 0
  bigLinesInstanced?: THREE.InstancedMesh
  smallLinesInstanced?: THREE.InstancedMesh
  bigLinesInitialX: number[] = []
  smallLinesInitialX: number[] = []
  sizes: Experience["sizes"]
  scrollWidth = 0
  noise: THREE.Texture
  debugFolder?: FolderApi

  constructor({
    scene,
    noise,
    debugFolder,
  }: {
    scene: THREE.Scene
    noise: THREE.Texture
    debugFolder?: FolderApi
  }) {
    this.scene = scene
    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes
    this.noise = noise
    this.debugFolder = debugFolder

    const element = document.querySelector("[data-animation='webgl-timeline']")
    if (!element) {
      throw new Error("Timeline element not found")
    }

    this.element = element as HTMLDivElement

    // Find the target container (the scrollable container)
    const targetContainer = document.querySelector("[data-target]")
    if (targetContainer) {
      this.targetContainer = targetContainer as HTMLElement
      // Store initial target position (before any GSAP transforms)
      this.storeTargetInitialPosition()
    }

    this.createBounds()
    this.updateFadeMargins()
    this.createBigLines()
    this.createSmallLines()

    this.setDebug()

    this.show()

    this.experience.on(EVENTS.About.Scroll, this.manageScroll.bind(this))
  }

  private storeTargetInitialPosition() {
    if (!this.targetContainer) {
      return
    }

    // Get target's position relative to trigger (untransformed)
    const trigger = document.querySelector("[data-trigger]")
    if (trigger) {
      const triggerBounds = trigger.getBoundingClientRect()
      const targetStyle = window.getComputedStyle(this.targetContainer)
      const targetLeft = targetStyle.left
      const targetRight = targetStyle.right
      const targetTop = targetStyle.top

      if (targetRight && targetRight !== "auto") {
        const rightValue = parseFloat(targetRight)
        this.targetInitialViewportX =
          triggerBounds.right - rightValue - this.targetContainer.offsetWidth
      } else if (targetLeft && targetLeft !== "auto") {
        this.targetInitialViewportX =
          triggerBounds.left + parseFloat(targetLeft)
      } else {
        this.targetInitialViewportX =
          triggerBounds.left + this.targetContainer.offsetLeft
      }

      if (targetTop && targetTop !== "auto") {
        this.targetInitialViewportY = triggerBounds.top + parseFloat(targetTop)
      } else {
        this.targetInitialViewportY =
          triggerBounds.top + this.targetContainer.offsetTop
      }
    } else {
      // Fallback: use getBoundingClientRect (should be at scroll 0 when created)
      const targetBounds = this.targetContainer.getBoundingClientRect()
      this.targetInitialViewportX = targetBounds.left
      this.targetInitialViewportY = targetBounds.top
    }
  }

  private updateFadeMargins() {
    const isMobile = this.sizes.width < MOBILE_BREAKPOINT
    settings.fadeStartMargin.value = isMobile
      ? MOBILE_FADE_START_MARGIN
      : DESKTOP_FADE_START_MARGIN
    settings.fadeEndMargin.value = isMobile
      ? MOBILE_FADE_END_MARGIN
      : DESKTOP_FADE_END_MARGIN
  }

  private createBounds() {
    // Get bounds relative to target container to avoid GSAP transform issues
    // Use offsetLeft/offsetTop to get position relative to offset parent (target container)
    if (
      this.targetContainer &&
      this.element.offsetParent === this.targetContainer
    ) {
      // Element is directly inside target, use offsetLeft/offsetTop
      const relativeX = this.element.offsetLeft
      const relativeY = this.element.offsetTop
      const width = this.element.offsetWidth
      const height = this.element.offsetHeight

      this.bounds = new DOMRect(relativeX, relativeY, width, height)
    } else {
      // Fallback: get bounds relative to target using getBoundingClientRect
      const elementBounds = this.element.getBoundingClientRect()
      if (this.targetContainer) {
        const targetBounds = this.targetContainer.getBoundingClientRect()
        const relativeX = elementBounds.x - targetBounds.x
        const relativeY = elementBounds.y - targetBounds.y

        this.bounds = new DOMRect(
          relativeX,
          relativeY,
          elementBounds.width,
          elementBounds.height,
        )
      } else {
        // Fallback to viewport-relative bounds
        this.bounds = elementBounds
      }
    }

    this.y = this.bounds.top
  }

  createBigLines() {
    const count = loader.about.length
    if (count === 0) {
      return
    }

    const geometry = new THREE.PlaneGeometry(
      settings.bigLineWidth.value,
      settings.bigLineHeight.value,
    )
    const material = new THREE.MeshBasicNodeMaterial()

    material.colorNode = settings.bigLineColor
    material.transparent = true

    const opacity = edgeFade(
      uniform(1),
      settings.fadeStartMargin,
      settings.fadeEndMargin,
      settings.overallOpacity,
    )

    material.opacityNode = opacity

    this.bigLinesInstanced = new THREE.InstancedMesh(geometry, material, count)
    this.bigLinesInstanced.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

    const { y } = this.getPosition()

    for (let i = 0; i < count; i++) {
      const x = this.getDotXPosition(i)
      this.bigLinesInitialX.push(x)

      dummyObject.position.set(x, y, 0)
      dummyObject.updateMatrix()
      this.bigLinesInstanced.setMatrixAt(i, dummyObject.matrix)
    }

    this.bigLinesInstanced.instanceMatrix.needsUpdate = true
    this.scene.add(this.bigLinesInstanced)
  }

  createSmallLines() {
    const bigLineCount = loader.about.length
    if (bigLineCount < 2) {
      return
    }

    const smallLinesPerGap = 10
    const count = (bigLineCount - 1) * smallLinesPerGap

    const geometry = new THREE.PlaneGeometry(
      settings.smallLineWidth.value,
      settings.smallLineHeight.value,
    )
    const material = new THREE.MeshBasicNodeMaterial()

    material.colorNode = settings.smallLineColor
    material.transparent = true

    const opacity = edgeFade(
      uniform(1),
      settings.fadeStartMargin,
      settings.fadeEndMargin,
      settings.overallOpacity,
    )

    material.opacityNode = opacity

    this.smallLinesInstanced = new THREE.InstancedMesh(
      geometry,
      material,
      count,
    )
    this.smallLinesInstanced.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

    const { y } = this.getPosition()

    let instanceIndex = 0
    for (let i = 0; i < bigLineCount - 1; i++) {
      const leftX = this.getDotXPosition(i)
      const rightX = this.getDotXPosition(i + 1)
      const gap = rightX - leftX
      const spacing = gap / (smallLinesPerGap + 1)

      for (let j = 1; j <= smallLinesPerGap; j++) {
        const x = leftX + spacing * j
        this.smallLinesInitialX.push(x)

        dummyObject.position.set(x, y, 0)
        dummyObject.updateMatrix()
        this.smallLinesInstanced.setMatrixAt(instanceIndex, dummyObject.matrix)
        instanceIndex++
      }
    }

    this.smallLinesInstanced.instanceMatrix.needsUpdate = true
    this.scene.add(this.smallLinesInstanced)
  }

  manageScroll(e: unknown) {
    if (!this.bigLinesInstanced) {
      return
    }

    const { progress, target } = e as { progress: number; target: HTMLElement }

    // Always recalculate scrollWidth to ensure it matches AboutLeaf's calculation
    // This ensures both move at the same velocity after resize
    this.scrollWidth = target.scrollWidth
    const scrollTo = this.scrollWidth - this.sizes.width
    const movementX = scaleValue(progress, [0, 1], [0, scrollTo])

    const { y } = this.getPosition()

    // Update big lines
    for (let i = 0; i < this.bigLinesInitialX.length; i++) {
      const newX = this.bigLinesInitialX[i] + movementX
      dummyObject.position.set(newX, y, 0)
      dummyObject.updateMatrix()
      this.bigLinesInstanced.setMatrixAt(i, dummyObject.matrix)
    }
    this.bigLinesInstanced.instanceMatrix.needsUpdate = true

    // Update small lines
    if (this.smallLinesInstanced) {
      for (let i = 0; i < this.smallLinesInitialX.length; i++) {
        const newX = this.smallLinesInitialX[i] + movementX
        dummyObject.position.set(newX, y, 0)
        dummyObject.updateMatrix()
        this.smallLinesInstanced.setMatrixAt(i, dummyObject.matrix)
      }
      this.smallLinesInstanced.instanceMatrix.needsUpdate = true
    }
  }

  getPosition() {
    // bounds.x is relative to target container, so add target's initial viewport position
    const boundLeft = this.bounds.x + this.targetInitialViewportX
    const boundTop = this.bounds.y + this.targetInitialViewportY
    const halfScreenWidth = this.sizes.width / 2
    const halfScreenHeight = this.sizes.height / 2
    const halfBoundsWidth = this.bounds.width / 2
    const halfBoundsHeight = this.bounds.height / 2

    return {
      x: boundLeft + halfBoundsWidth - halfScreenWidth,
      y: -boundTop + halfScreenHeight - halfBoundsHeight,
    }
  }

  getDotXPosition(index: number) {
    // bounds.x is relative to target container, so add target's initial viewport position
    const left = this.bounds.x + this.targetInitialViewportX
    const right =
      this.bounds.x + this.bounds.width + this.targetInitialViewportX
    const count = loader.about.length
    const t = count === 1 ? 0.5 : index / (count - 1)
    return left + t * (right - left) - this.sizes.width / 2
  }

  resize() {
    // Recalculate target's initial position (in case layout changed)
    if (this.targetContainer) {
      this.storeTargetInitialPosition()
    }

    // Recalculate bounds
    this.createBounds()

    // Update fade margins based on screen size
    this.updateFadeMargins()

    // Reset scrollWidth so it gets recalculated with new dimensions
    // This ensures it matches AboutLeaf's scrollWidth calculation
    this.scrollWidth = 0

    // Recalculate initial X positions (but don't set matrices yet - let manageScroll do that)
    if (this.bigLinesInstanced) {
      this.bigLinesInitialX = []
      for (let i = 0; i < loader.about.length; i++) {
        const x = this.getDotXPosition(i)
        this.bigLinesInitialX.push(x)
      }
    }

    // Recalculate small lines initial X positions
    if (this.smallLinesInstanced) {
      this.smallLinesInitialX = []
      const bigLineCount = loader.about.length
      const smallLinesPerGap = 10

      for (let i = 0; i < bigLineCount - 1; i++) {
        const leftX = this.getDotXPosition(i)
        const rightX = this.getDotXPosition(i + 1)
        const gap = rightX - leftX
        const spacing = gap / (smallLinesPerGap + 1)

        for (let j = 1; j <= smallLinesPerGap; j++) {
          const x = leftX + spacing * j
          this.smallLinesInitialX.push(x)
        }
      }
    }
  }

  async show() {
    //
    // Animation out
    const alpha = { value: settings.overallOpacity.value }

    await new Promise((resolve) => {
      gsap.to(alpha, {
        value: 1,
        duration: 1,
        ease: "expo.in",
        onUpdate: () => {
          settings.overallOpacity.value = alpha.value
        },
        onComplete: resolve,
      })
    })
  }

  async leave() {
    //
    // Animation out
    const alpha = { value: settings.overallOpacity.value }

    await new Promise((resolve) => {
      gsap.to(alpha, {
        value: 0,
        duration: 1,
        onUpdate: () => {
          settings.overallOpacity.value = alpha.value
        },
        onComplete: resolve,
      })
    })

    // Dispose big lines
    if (this.bigLinesInstanced) {
      this.scene.remove(this.bigLinesInstanced)
      this.bigLinesInstanced.geometry.dispose()
      ;(
        this.bigLinesInstanced.material as THREE.MeshBasicNodeMaterial
      ).dispose()
      this.bigLinesInstanced = undefined
    }

    // Dispose small lines
    if (this.smallLinesInstanced) {
      this.scene.remove(this.smallLinesInstanced)
      this.smallLinesInstanced.geometry.dispose()
      ;(
        this.smallLinesInstanced.material as THREE.MeshBasicNodeMaterial
      ).dispose()
      this.smallLinesInstanced = undefined
    }

    // Clear position arrays
    this.bigLinesInitialX = []
    this.smallLinesInitialX = []
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "⌛ Timeline",
      expanded: false,
    })

    // Edge fade controls
    const fadeSettings = {
      fadeStartMargin: settings.fadeStartMargin.value,
      fadeEndMargin: settings.fadeEndMargin.value,
    }

    folder
      .addBinding(fadeSettings, "fadeStartMargin", {
        label: "Fade Start Margin",
        min: 0,
        max: 0.5,
        step: 0.01,
      })
      .on("change", (ev) => {
        settings.fadeStartMargin.value = ev.value
      })

    folder
      .addBinding(fadeSettings, "fadeEndMargin", {
        label: "Fade End Margin",
        min: 0,
        max: 0.5,
        step: 0.01,
      })
      .on("change", (ev) => {
        settings.fadeEndMargin.value = ev.value
      })

    // Big lines folder
    const bigLinesFolder = folder.addFolder({
      title: "Big Lines",
      expanded: false,
    })

    const bigLineSettings = {
      height: settings.bigLineHeight.value,
      width: settings.bigLineWidth.value,
      color: `#${new THREE.Color(settings.bigLineColor.value).getHexString()}`,
    }

    bigLinesFolder
      .addBinding(bigLineSettings, "height", {
        min: 10,
        max: 200,
        step: 1,
      })
      .on("change", (ev) => {
        settings.bigLineHeight.value = ev.value
        // Recreate geometry if mesh exists
        if (this.bigLinesInstanced) {
          const oldGeometry = this.bigLinesInstanced.geometry
          const newGeometry = new THREE.PlaneGeometry(
            settings.bigLineWidth.value,
            ev.value,
          )
          this.bigLinesInstanced.geometry = newGeometry
          oldGeometry.dispose()
        }
      })

    bigLinesFolder
      .addBinding(bigLineSettings, "width", {
        min: 0.5,
        max: 10,
        step: 0.1,
      })
      .on("change", (ev) => {
        settings.bigLineWidth.value = ev.value
        // Recreate geometry if mesh exists
        if (this.bigLinesInstanced) {
          const oldGeometry = this.bigLinesInstanced.geometry
          const newGeometry = new THREE.PlaneGeometry(
            ev.value,
            settings.bigLineHeight.value,
          )
          this.bigLinesInstanced.geometry = newGeometry
          oldGeometry.dispose()
        }
      })

    bigLinesFolder
      .addBinding(bigLineSettings, "color", {
        view: "color",
      })
      .on("change", (ev) => {
        settings.bigLineColor.value = new THREE.Color(ev.value)
      })

    // Small lines folder
    const smallLinesFolder = folder.addFolder({
      title: "Small Lines",
      expanded: false,
    })

    const smallLineSettings = {
      height: settings.smallLineHeight.value,
      width: settings.smallLineWidth.value,
      color: `#${new THREE.Color(settings.smallLineColor.value).getHexString()}`,
    }

    smallLinesFolder
      .addBinding(smallLineSettings, "height", {
        min: 10,
        max: 100,
        step: 1,
      })
      .on("change", (ev) => {
        settings.smallLineHeight.value = ev.value
        // Recreate geometry if mesh exists
        if (this.smallLinesInstanced) {
          const oldGeometry = this.smallLinesInstanced.geometry
          const newGeometry = new THREE.PlaneGeometry(
            settings.smallLineWidth.value,
            ev.value,
          )
          this.smallLinesInstanced.geometry = newGeometry
          oldGeometry.dispose()
        }
      })

    smallLinesFolder
      .addBinding(smallLineSettings, "width", {
        min: 0.5,
        max: 5,
        step: 0.1,
      })
      .on("change", (ev) => {
        settings.smallLineWidth.value = ev.value
        // Recreate geometry if mesh exists
        if (this.smallLinesInstanced) {
          const oldGeometry = this.smallLinesInstanced.geometry
          const newGeometry = new THREE.PlaneGeometry(
            ev.value,
            settings.smallLineHeight.value,
          )
          this.smallLinesInstanced.geometry = newGeometry
          oldGeometry.dispose()
        }
      })

    smallLinesFolder
      .addBinding(smallLineSettings, "color", {
        view: "color",
      })
      .on("change", (ev) => {
        settings.smallLineColor.value = new THREE.Color(ev.value)
      })
  }
}
